<template>
    <div v-if="$ability.hasPermission('catalog:UpdateDisplayMode')">
        <div class="grid grid-cols-2 gap-2">
            <button
                v-for="option in optionsWithState"
                :key="option.value"
                type="button"
                :disabled="isSaving"
                class="relative flex flex-col items-center gap-1.5 rounded-lg border p-2 transition disabled:opacity-60"
                :class="
                    option.value === displayMode
                        ? 'border-theme-primary bg-theme-hover'
                        : 'border-theme-border hover:bg-theme-hover'
                "
                @click="selectOption(option.value)"
            >
                <span
                    v-if="option.state === 'locked'"
                    class="absolute right-1.5 top-1 text-xs"
                >
                    🔒
                </span>
                <span
                    v-else-if="option.state === 'needsProducts'"
                    class="absolute right-1.5 top-1 text-xs"
                    title="Activa Productos en Mis funciones"
                >
                    🧩
                </span>
                <display-mode-silhouette
                    class="h-10 w-14"
                    :class="
                        option.value === displayMode
                            ? 'text-theme-primary'
                            : 'text-theme-muted'
                    "
                    :shapes="option.silhouette"
                />
                <span
                    class="text-xs font-medium"
                    :class="
                        option.value === displayMode
                            ? 'text-theme-primary'
                            : 'text-theme-muted'
                    "
                >
                    {{ option.icon }} {{ option.label }}
                </span>
            </button>
        </div>
        <p class="mt-1.5 text-xs text-theme-muted">{{ currentDescription }}</p>
        <p v-if="needsProducts" class="mt-1 text-xs text-theme-warning">
            Para usar Catálogo o Mixto, activa Productos en
            <router-link to="/app/settings/features" class="underline">
                Mis funciones
            </router-link>
            .
        </p>

        <display-mode-confirm-modal
            v-model="confirmOpen"
            :option="pendingOption"
            :segments-count="segmentsCount"
            :current-display-mode="displayMode"
            :is-saving="isSaving"
            @confirm="confirmChange"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import {
    DISPLAY_MODE_OPTIONS,
    findDisplayModeOption
} from "@/common/fields/display-modes";
import {
    DISPLAY_MODE_STATES,
    canShowUpgrade,
    displayModeLockFeature,
    displayModeState,
    isPlanManaged
} from "@/common/fields/plan-features";

import DisplayModeConfirmModal from "@/components/editor/DisplayModeConfirmModal.vue";
import DisplayModeSilhouette from "@/components/editor/DisplayModeSilhouette.vue";

export default {
    name: "DisplayModeSwitch",
    components: {
        DisplayModeConfirmModal,
        DisplayModeSilhouette
    },
    props: {
        displayMode: {
            type: String,
            default: ""
        },
        segmentsCount: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            isSaving: false,
            options: DISPLAY_MODE_OPTIONS,
            confirmOpen: false,
            pendingOption: null
        };
    },
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        ...mapState("auth", {
            planPermissions: "permissions",
            featureFlags: "featureFlags"
        }),
        // billing-fase5-salida-planes: cada vista distinta de `feed` depende
        // del plan (y las de productos, de tener Productos activado). Una
        // vista bloqueada solo se muestra con su `upgrade:Feature<X>`, salvo
        // que sea la vista actual (portal-sesion-y-candados-optimizacion).
        optionsWithState() {
            return this.options
                .map((option) => {
                    const state = displayModeState(
                        option.value,
                        this.planPermissions,
                        this.featureFlags
                    );
                    const lockFeature =
                        state === DISPLAY_MODE_STATES.locked
                            ? displayModeLockFeature(
                                  option.value,
                                  this.planPermissions
                              )
                            : null;
                    return { ...option, state, lockFeature };
                })
                .filter(
                    (option) =>
                        option.state !== DISPLAY_MODE_STATES.locked ||
                        option.value === this.displayMode ||
                        canShowUpgrade(this.planPermissions, option.lockFeature)
                );
        },
        needsProducts() {
            return this.optionsWithState.some(
                (option) => option.state === DISPLAY_MODE_STATES.needsProducts
            );
        },
        currentDescription() {
            const option = findDisplayModeOption(this.displayMode);
            return option ? option.description : "";
        }
    },
    // Los flags de la sesión vienen del login y no se enteran de cambios
    // hechos después (plan nuevo, otra pestaña): sin refrescarlos, Catálogo
    // y Mixto quedaban bloqueados con Productos ya activado.
    // Solo multitenant con planes tiene "Mis funciones": en ecommerce el
    // endpoint no existe para el rol y respondía 403.
    async mounted() {
        if (!isPlanManaged(this.planPermissions)) return;
        try {
            const flags = await this.getFeatureFlagsSettings();
            this.applyFeatureFlags({ ...this.featureFlags, ...flags });
        } catch {
            // Se mantienen los flags de la sesión
        }
    },
    methods: {
        ...mapActions("tenants", [
            "updateTenantDisplayMode",
            "getFeatureFlagsSettings"
        ]),
        ...mapActions("auth", ["applyFeatureFlags"]),
        ...mapActions("platformSubscriptions", ["openPlanUpgrade"]),
        selectOption(displayMode) {
            if (displayMode === this.displayMode || this.isSaving) return;

            const option = this.optionsWithState.find(
                (item) => item.value === displayMode
            );
            if (option && option.state === DISPLAY_MODE_STATES.locked) {
                this.openPlanUpgrade({
                    kind: "feature",
                    feature: option.lockFeature
                });
                return;
            }
            // Productos apagado en "Mis funciones": se explica en vez de
            // ignorar el clic (antes el botón quedaba deshabilitado sin más).
            if (option && option.state === DISPLAY_MODE_STATES.needsProducts) {
                this.explainNeedsProducts(option);
                return;
            }

            this.pendingOption = findDisplayModeOption(displayMode);
            this.confirmOpen = true;
        },
        async explainNeedsProducts(option) {
            const { isConfirmed } = await this.$swal.fire({
                background: this.backgroundColor,
                icon: "info",
                title: `Para usar ${option.label}, activa Productos`,
                text: "La función Productos está apagada para tu catálogo. Actívala en Mis funciones y vuelve a elegir esta vista.",
                showCancelButton: true,
                confirmButtonText: "Ir a Mis funciones",
                cancelButtonText: "Ahora no"
            });
            if (isConfirmed) this.$router.push("/app/settings/features");
        },
        async confirmChange() {
            const displayMode = this.pendingOption?.value;
            if (!displayMode) return;

            this.isSaving = true;
            try {
                await this.updateTenantDisplayMode({ displayMode });
                this.isSaving = false;
                this.confirmOpen = false;
                this.$sound.play("success");
                this.$emit("updated", displayMode);
            } catch (error) {
                this.isSaving = false;
                this.confirmOpen = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
